import {
  OnboardingTarget,
  onboardingTarget,
} from "../onboarding/onboardingTargets";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useContext, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  selectSelectedTakeId,
  selectTakes3D,
} from "../../app/slices/TrainingSlice";
import { selectErrors, selectWarnings } from "../../app/slices/ValidationSlice";
import {
  EditorColumnFooterButton,
  EditorColumnHeader,
} from "../editor_columns/EditorColumn";
import { CopyPasteContext } from "../training_timeline/CopyPasteContext";
import StoryboardTakeCard3D from "./StoryboardTakeCard3D";
import { getTakeIssueSummary } from "./takeIssueSummary";
import { useAddTake3D } from "./useAddTake3D";
import {
  getTakeDndId,
  useTakeInteractionDndContext,
} from "../training_timeline/TakeInteractionDndProvider";

export default function StoryboardTimeline3D() {
  const takes = useSelector(selectTakes3D);
  const selectedTakeId = useSelector(selectSelectedTakeId);
  const warnings = useSelector(selectWarnings);
  const errors = useSelector(selectErrors);
  const copyPasteContext = useContext(CopyPasteContext);
  const takeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { addTake } = useAddTake3D();
  const { hoverTakeId } = useTakeInteractionDndContext();

  // Clipboard shortcuts for the 3D editor are bound by
  // TimelineClipboardProvider, which stays mounted while this storyboard is
  // swapped out for the quiz feedback rail.

  useEffect(() => {
    if (!selectedTakeId) return;

    takeRefs.current[selectedTakeId]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [selectedTakeId]);

  const issueSummaries = useMemo(
    () =>
      takes.map((take) => ({
        takeId: take.id,
        summary: getTakeIssueSummary(take, warnings, errors),
      })),
    [errors, takes, warnings]
  );

  const issueSummaryByTake = useMemo(() => {
    return Object.fromEntries(
      issueSummaries.map(({ takeId, summary }) => [takeId, summary])
    );
  }, [issueSummaries]);

  return (
    <div
      className="storyboard-timeline w-[250px] min-w-[250px] h-full min-h-0 flex flex-col bg-creator-surface border-r border-creator-border text-creator-text"
      data-testid="storyboard-timeline-3d"
      {...onboardingTarget(OnboardingTarget.Editor3DStoryboard)}
      tabIndex={0}
      onFocus={() => copyPasteContext?.setPasteContext("timeline")}
    >
      <EditorColumnHeader
        icon={<DashboardOutlinedIcon fontSize="inherit" />}
        title="Storyboard"
        testId="storyboard-timeline-header"
      />
      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-2.5 space-y-1.5 pit-creator-scrollbar">
        <SortableContext
          items={takes.map((take) => getTakeDndId(take.id))}
          strategy={verticalListSortingStrategy}
          id="storyboard-takes3d"
        >
          {takes.map((take, index) => (
            <div
              ref={(ref) => (takeRefs.current[take.id] = ref)}
              key={take.id}
            >
              <StoryboardTakeCard3D
                take={take}
                index={index}
                isSelected={selectedTakeId === take.id}
                isDropTarget={hoverTakeId === take.id}
                issueSummary={issueSummaryByTake[take.id]}
              />
            </div>
          ))}
        </SortableContext>
      </div>
      <div className="flex-shrink-0 p-2.5 border-t border-creator-border">
        <EditorColumnFooterButton
          label="Add take"
          onClick={addTake}
          testId="storyboard-timeline-add-take"
        />
      </div>
    </div>
  );
}
